import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { api, errMsg, COUNTRIES } from "@/lib/api";
import { toast } from "sonner";
import { Globe2, Send, CheckCircle2, Pill } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function PublicEnquiry() {
  const { slug } = useParams();
  const [company, setCompany] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [f, setF] = useState({ buyer_company: "", contact_person: "", email: "", phone: "", country: "", product_id: "", message: "" });
  const up = (k, v) => setF({ ...f, [k]: v });

  useEffect(() => {
    api.get(`/public/company/${slug}`).then(({ data }) => setCompany(data)).catch(() => setNotFound(true));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post(`/public/company/${slug}/enquiry`, f);
      setSent(true);
      toast.success(data.message);
    } catch (err) { toast.error(errMsg(err)); } finally { setSending(false); }
  };

  if (notFound) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500">Company page not found.</div>;
  if (!company) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-sky-500" /></div>;

  const accent = company.accent_color || "#0284c7";
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="flex items-center gap-2.5 mb-8">
            {company.logo ? (
              <img src={company.logo} alt={company.name} className="h-9 w-auto max-w-[160px] object-contain" />
            ) : (
              <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: accent }}><Globe2 className="h-5 w-5" /></div>
            )}
            <p className="font-bold font-display">{company.name}</p>
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold font-display max-w-3xl leading-tight">Request an export quotation from {company.name}</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">Browse our international export catalogue and send an enquiry. Our export team responds to every international buyer promptly.</p>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          <h2 className="text-xl font-semibold text-slate-800 font-display mb-4">Product Catalogue</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="public-products">
            {company.products.map((p) => (
              <Card key={p.id} className="p-4 border border-slate-200/80 rounded-xl hover:shadow-md transition-shadow cursor-pointer" onClick={() => up("product_id", p.id)} data-testid={`public-product-${p.id}`}>
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-sky-50 flex items-center justify-center flex-shrink-0"><Pill className="h-5 w-5 text-sky-600" /></div>
                  <div>
                    <p className="font-medium text-slate-800">{p.name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{p.generic_name} · {p.dosage_form} {p.strength}</p>
                    {p.category && <span className="inline-block mt-2 text-[10px] uppercase tracking-wide font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{p.category}</span>}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2">
          <Card className="p-6 border border-slate-200/80 rounded-xl shadow-sm sticky top-6">
            {sent ? (
              <div className="text-center py-8" data-testid="enquiry-success">
                <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
                <h3 className="mt-4 font-semibold text-slate-800 font-display text-lg">Enquiry received</h3>
                <p className="mt-2 text-sm text-slate-500">Thank you! The export team will contact you shortly.</p>
                <Button className="mt-5" variant="outline" onClick={() => { setSent(false); setF({ buyer_company: "", contact_person: "", email: "", phone: "", country: "", product_id: "", message: "" }); }}>Send another enquiry</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3.5">
                <h3 className="font-semibold text-slate-800 font-display text-lg">Send an enquiry</h3>
                <div className="space-y-1.5"><Label className="text-xs">Your company *</Label><Input required value={f.buyer_company} onChange={(e) => up("buyer_company", e.target.value)} className="bg-white" data-testid="enquiry-company" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Contact person</Label><Input value={f.contact_person} onChange={(e) => up("contact_person", e.target.value)} className="bg-white" data-testid="enquiry-contact" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input type="email" value={f.email} onChange={(e) => up("email", e.target.value)} className="bg-white" data-testid="enquiry-email" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Phone</Label><Input value={f.phone} onChange={(e) => up("phone", e.target.value)} className="bg-white" data-testid="enquiry-phone" /></div>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Country</Label>
                  <Select value={f.country} onValueChange={(v) => up("country", v)}><SelectTrigger className="bg-white" data-testid="enquiry-country"><SelectValue placeholder="Select country" /></SelectTrigger><SelectContent className="bg-white max-h-60">{COUNTRIES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Product of interest</Label>
                  <Select value={f.product_id} onValueChange={(v) => up("product_id", v)}><SelectTrigger className="bg-white" data-testid="enquiry-product"><SelectValue placeholder="Select product" /></SelectTrigger><SelectContent className="bg-white max-h-60">{company.products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Message</Label><Textarea value={f.message} onChange={(e) => up("message", e.target.value)} rows={3} placeholder="Quantity, destination port, requirements…" className="bg-white" data-testid="enquiry-message" /></div>
                <Button type="submit" disabled={sending} style={{ backgroundColor: accent }} className="w-full hover:opacity-90 text-white gap-2" data-testid="enquiry-submit"><Send className="h-4 w-4" />{sending ? "Sending…" : "Send enquiry"}</Button>
              </form>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
